import React from 'react';
import { Box } from '@mui/material';

import {
  SalonPublicThemeProvider,
  useSalonPublicTheme,
} from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateIdentity from '../blocks/SalonTemplateIdentity';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const EcrinSereinTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { config, layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasReviews = Boolean(
    reviews.length > 0 || publicState.reviewsLoading || publicState.reviewsError,
  );
  const hasServices = Boolean(
    publicState.servicesLoading
    || viewModel?.serviceCategories?.some((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    )),
  );
  const centeredLayoutConfig = {
    ...layout.config,
    header: { ...layout.config.header, composition: 'centered' },
    content: { ...layout.config.content, alignment: 'center' },
  };

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="ecrin_serein"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <Box
        data-testid="ecrin-serein-identity"
        data-alignment="center"
      >
        <SalonPublicThemeProvider
          config={config}
          layoutConfig={centeredLayoutConfig}
          templateKey="ecrin_serein"
          direction={direction}
        >
          <SalonTemplateIdentity
            data={{
              ...identity,
              heroPhoto: photos[0]?.src,
              averageRating: viewModel?.averageRating,
              reviewCount: reviews.length,
              isFavorite: viewModel?.capabilities?.isFavorite,
            }}
            variant="immersive"
            mode={mode}
            onBack={actions?.onBack}
            onToggleFavorite={actions?.onToggleFavorite}
            onOpenMobileInfo={actions?.onOpenMobileInfo}
            onOpenHeaderInfo={actions?.onOpenHeaderInfo}
            onBook={actions?.onOpenBooking}
            onContact={actions?.onContact}
            clientChatEnabled={identity.raw?.client_chat_enabled}
          />
        </SalonPublicThemeProvider>
      </Box>

      <Box
        data-testid="ecrin-serein-scroll-region"
        sx={{
          flex: 1,
          minHeight: mobile ? 'auto' : 0,
          width: '100%',
          overflowY: mobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <Box
          component="main"
          data-testid="ecrin-serein-content"
          data-layout={mobile ? 'single-column' : 'contemplative-stack'}
          sx={{
            width: '100%',
            maxWidth: 1160,
            mx: 'auto',
            px: { xs: 2, md: 6 },
            py: { xs: 4, md: 8 },
            boxSizing: 'border-box',
          }}
        >
          {identity.description ? (
            <Box
              data-testid="ecrin-serein-about"
              sx={{ maxWidth: 880, mx: 'auto', mb: { xs: 3, md: 6 } }}
            >
              <SalonTemplateAbout data={identity} variant="panel" />
            </Box>
          ) : null}
          {hasServices ? (
            <Box
              data-testid="ecrin-serein-experiences"
              sx={{
                maxWidth: 960,
                mx: 'auto',
                '& [data-testid="salon-public-services"]': { gap: { xs: 3, md: 6 } },
              }}
            >
              <SalonTemplateServices
                data={viewModel?.serviceCategories}
                variant="experiences"
                mode={mode}
                viewport={viewport}
                loading={Boolean(publicState.servicesLoading)}
                onBookService={actions?.onBookService}
              />
            </Box>
          ) : null}
          {visibility.gallery && (photos.length > 0 || mode === 'preview') ? (
            <Box
              data-testid="ecrin-serein-gallery"
              sx={{ maxWidth: 960, mx: 'auto' }}
            >
              <SalonTemplateGallery
                data={photos}
                salonId={identity.id}
                variant="editorial"
                visible={visibility.gallery}
                mode={mode}
                onSelectPhoto={actions?.onSelectPhoto}
                onNavigatePhoto={actions?.onNavigatePhoto}
                selectedIndex={publicState.photoIndex}
              />
            </Box>
          ) : null}
          {hasReviews ? (
            <SalonTemplateReviews
              data={{
                items: reviews,
                averageRating: viewModel?.averageRating,
                isAuthenticated: Boolean(publicState.isAuthenticated),
                isLoading: Boolean(publicState.reviewsLoading),
                error: publicState.reviewsError || null,
              }}
              variant="quotes"
              visible={visibility.reviews}
              mode={mode}
              onOpenReviewComposer={actions?.onOpenReviewComposer}
              onOpenAllReviews={actions?.onOpenAllReviews}
            />
          ) : null}
          <SalonTemplateTeam
            data={viewModel?.team}
            variant="cards"
            visible={visibility.team}
            mode={mode}
            onSelectMember={actions?.onSelectTeamMember}
          />
          <SalonTemplateOpeningHours
            data={{ hours: viewModel?.openingHours, closures: viewModel?.closures }}
            variant="card"
            visible={visibility.opening_hours}
            markClosuresAsBlock={false}
          />
          <SalonTemplateContactLocation
            data={identity}
            variant="split"
            visible={visibility.location}
            mode={mode}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EcrinSereinTemplate;
